import { useMemo, useState } from 'react';

import { HelpCircleIcon, Mail01Icon, MailOpen01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { checkHistoryActions } from 'constants/check-history-actions';
import { CommunicationHistoryTypes } from 'constants/check-history-types';
import { emailTemplateTypeForHistoryEntry } from 'constants/history-email-template-types';
import { useLocale } from 'hooks/locale';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';

import { HistoryEmailTemplateModal } from 'components/HistoryEmailTemplateModal/HistoryEmailTemplateModal';
import { TitleGroup } from 'components/TitleGroup/TitleGroup';

import { useGetCandidateHistoryQuery } from 'api/candidates-api';

const COLOR_MAP = {
  'green-2': 'text-green-2',
  'yellow-2': 'text-yellow-2',
  'blue-2': 'text-blue-2',
  'red-2': 'text-red-2',
  'primary-4': 'text-primary-4',
  'orange-2': 'text-orange-2',
};

const buildActionData = (entry, { candidate, companyChecks, referees }) => {
  const checkHuman = entry?.check_type
    ? companyChecks?.find(cc => cc.type === entry.check_type)?.check_type?.title
    : null;
  const referee = entry.referee_id ? referees?.find(r => r.id === entry.referee_id) : null;

  return {
    refCandidate: <b>{candidate?.name}</b>,
    refReferee: referee ? <b>{referee.name}</b> : null,
    referenceLink: null,
    candidateRefereeLinks: null,
    country: entry.country && entry.country !== 'Not specified.' ? `from ${entry.country}` : '',
    styles: {},
    userName: <b>@{entry.user_name || 'User'}&#160;</b>,
    candidate,
    checkHuman,
    entry,
    referee,
    onCandidateRequest: () => {},
    onRefereeRequest: () => {},
    referees,
    companyUsers: [],
  };
};

export const CommunicationHistoryList = ({
  candidate,
  company,
  brand,
  companyChecks = [],
  referees = [],
}) => {
  const { t } = useTranslation();
  const { dateSlashFormat } = useLocale(company?.locale);
  const [emailTemplateEntry, setEmailTemplateEntry] = useState(null);
  const [emailTemplateModalOpen, setEmailTemplateModalOpen] = useState(false);

  const handleViewEmailTemplate = entry => {
    setEmailTemplateEntry(entry);
    setEmailTemplateModalOpen(true);
  };

  // Same query args as History tab — RTK Query shares one cached response.
  const { data: entries, isLoading } = useGetCandidateHistoryQuery(
    { id: candidate.id, params: { internal: false } },
    { skip: !candidate?.id },
  );

  const communications = useMemo(() => {
    if (!entries?.length) return [];
    return entries.filter(entry => entry && CommunicationHistoryTypes.has(Number(entry.type)));
  }, [entries]);

  return (
    <div className="bg-neutral-1 p-8 rounded-3xl border border-neutral-4 shadow-sm flex flex-col gap-8 min-h-0">
      <div className="flex items-start justify-between gap-4">
        <TitleGroup
          icon={Mail01Icon}
          title="Communication history"
          subTitle="Emails, SMS, reminders, and requests for this candidate"
        />
        <Link
          to={`/check/${candidate.id}/history`}
          className="text-primary-4 text-lg font-medium shrink-0 whitespace-nowrap">
          View all
        </Link>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="w-5 h-5 rounded-full bg-neutral-3 shrink-0" />
              <div className="flex-1 flex flex-col gap-2">
                <div className="w-24 h-4 bg-neutral-3 rounded" />
                <div className="w-full h-4 bg-neutral-3 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : communications.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-8">
          <div className="flex flex-col gap-2 rounded-2xl bg-neutral-2 p-8">
            <p className="text-text-secondary text-[14px] font-medium u-margin-0">
              No communication history yet
            </p>
            <span className="text-text-tertiary text-[12px] font-light">
              Communication requests sent to the candidate and referees will appear here once they
              have been recorded.
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-8 max-h-[60vh] overflow-y-auto -mx-2 px-2">
          {communications.map(entry => {
            const actionData = buildActionData(entry, { candidate, companyChecks, referees });
            const action = checkHistoryActions[entry.type]
              ? checkHistoryActions[entry.type](actionData)
              : { icon: HelpCircleIcon, description: <p>Unknown action.</p>, color: null };
            const iconColorClass = action.color
              ? COLOR_MAP[action.color] || 'text-text-primary'
              : 'text-text-primary';
            const emailType = emailTemplateTypeForHistoryEntry(entry.type);

            return (
              <div className="flex justify-between items-start gap-4 w-full bg-neutral-2 p-4 rounded-2xl">
                <div key={entry.id} className="flex gap-4 items-start">
                  <HugeiconsIcon
                    icon={action.icon || HelpCircleIcon}
                    size="18"
                    strokeWidth={1.5}
                    className={`${iconColorClass} shrink-0 mt-1`}
                  />
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-text-tertiary text-[12px] font-light">
                      <Moment format={dateSlashFormat}>{entry.created_at}</Moment>
                      <Moment format="@HH:mm">{entry.created_at}</Moment>
                    </div>
                    <div className="text-text-primary text-[14px] font-light leading-snug [&>p]:u-margin-0">
                      {action.description}
                    </div>
                  </div>
                </div>
                {emailType && (
                  <OverlayTrigger placement="top" overlay={<Tooltip>View email template</Tooltip>}>
                    <HugeiconsIcon
                      icon={MailOpen01Icon}
                      size="18"
                      strokeWidth={1.5}
                      className="text-primary-4 shrink-0 mt-1 cursor-pointer"
                      onClick={() => handleViewEmailTemplate(entry)}
                    />
                  </OverlayTrigger>
                )}
              </div>
            );
          })}
        </div>
      )}

      <HistoryEmailTemplateModal
        visible={emailTemplateModalOpen}
        onClose={() => setEmailTemplateModalOpen(false)}
        brand={brand}
        emailType={
          emailTemplateEntry ? emailTemplateTypeForHistoryEntry(emailTemplateEntry.type) : null
        }
        emailTemplateId={emailTemplateEntry?.email_template_id}
      />
    </div>
  );
};
